/**
 * The tab's identity: who is signed in, and whether trading is armed.
 *
 * Two independent lifecycles, held in one store because one component (the
 * header) renders both and one component (the ticket) gates on both — but
 * **never merged into a single enum**, because docs/04 §3.1 is explicit that
 * they are decoupled:
 *
 * > Session expiry and session-key expiry are decoupled. Losing the browser
 * > session must not revoke the trading key, and revoking the trading key must
 * > not log the user out.
 *
 * A fused `IDENTITY_STATE` would make "signed in but not armed" and "armed but
 * signed out" the same axis, and the second of those is a real state: a user
 * whose cookie expired still holds a valid key, and must be able to sign back in
 * without a second wallet popup. That is the difference between one signature
 * per week and two.
 *
 * ```
 *   session:  ANONYMOUS ─connect─▶ CONNECTED ─siwe─▶ AUTHENTICATED
 *                   ▲                                      │
 *                   └──────────── logout / expiry ─────────┘
 *
 *   key:      ABSENT ─arm (1 popup)─▶ ARMED ─T-10min─▶ ROTATE_DUE ─T+20─▶ ARMED
 *                   ▲                                                       │
 *                   └────────── revoke / expiry / reconcile ────────────────┘
 * ```
 *
 * This is a plain `ExternalStore`, not context state, for the reason plan §2.2
 * gives: `useStore` is `useSyncExternalStore`, so a subscription hoisted up the
 * tree re-renders everything below it. The header subscribes; the ticket
 * subscribes; the market panels do not, and a sign-in does not reconcile the
 * orderbook.
 */

import { ExternalStore } from '@/stores/store'
import type { AuthenticatedSession, Session } from './types'
import type { KeyState } from './rotation'

export type SessionPhase =
  /** No wallet connected. The market page's default, and a complete state. */
  | 'ANONYMOUS'
  /** Wallet connected, no `ud_session` yet — between `eth_requestAccounts` and SIWE. */
  | 'CONNECTED'
  /** Signing in: nonce fetched, `personal_sign` outstanding. */
  | 'SIGNING_IN'
  /** `ud_session` established. */
  | 'AUTHENTICATED'

export interface IdentityState {
  phase: SessionPhase
  /**
   * The account this tab acts as: the connected wallet before SIWE, the
   * session's address afterwards. **This is what becomes an order's `maker`**
   * (`trading/order-controller.ts` → `wallet/order.ts`), which is why the two
   * sources must be reconciled rather than merely overwritten — see
   * `walletAddress` and `accountMismatch` below.
   */
  address: string | null
  /**
   * What the *wallet* says it is connected as, right now.
   *
   * Held separately and written **only from the wallet**, never from a server
   * response, so the two can be compared. `address` alone cannot answer the
   * question, because `applySession` replaces it with the session's address and
   * the disagreement disappears at exactly the moment it starts mattering
   * (`docs/17`). `null` means no wallet stack is loaded — the anonymous cold
   * load, where invariant 13 forbids one — and is never a mismatch.
   */
  walletAddress: string | null
  /**
   * The chain the *wallet* is on, written only by `applyWallet` for
   * `walletAddress`'s reason. `applySession` used to overwrite it with the
   * session's `chain_id`, which made a wallet on the wrong network invisible here.
   */
  chainId: number | null
  session: AuthenticatedSession | null

  key: KeyState
  keyAddress: string | null
  keyExpiresAtMs: number | null
  /** True while a wallet popup is on screen — the ticket must not raise a second. */
  walletBusy: boolean

  /**
   * Why the local key was dropped, when it was dropped for a reason the user
   * should hear. Cleared once shown. `null` is the normal case, including after
   * a clean revoke the user performed themselves.
   */
  keyNotice: string | null
  error: unknown
}

export const INITIAL_IDENTITY: IdentityState = {
  phase: 'ANONYMOUS',
  address: null,
  walletAddress: null,
  chainId: null,
  session: null,
  key: 'ABSENT',
  keyAddress: null,
  keyExpiresAtMs: null,
  walletBusy: false,
  keyNotice: null,
  error: null,
}

export class IdentityStore extends ExternalStore<IdentityState> {
  constructor(initial: IdentityState = INITIAL_IDENTITY) {
    super(initial)
  }

  /** Patch and publish in one turn. Identity changes are rare — order of tens
   *  per session — so there is no rAF batching here and none is wanted. */
  patch(next: Partial<IdentityState>): void {
    this.set({ ...this.state, ...next })
    this.notify()
  }

  /**
   * What the wallet is connected as. The only writer of `walletAddress`.
   *
   * Called on connect, before each of the two signing popups, and from the
   * wallet's own account-change event — a user switching account in MetaMask
   * mid-session is the case that has no other signal, and before this existed
   * nothing in the tree read `accountsChanged` at all.
   */
  applyWallet(input: { address: string | null; chainId?: number | null }): void {
    this.patch({
      walletAddress: input.address,
      ...(input.chainId === undefined ? {} : { chainId: input.chainId }),
    })
  }

  applySession(session: Session | null): void {
    const authenticated = Boolean(session?.authenticated && session.address)
    if (!authenticated) {
      this.patch({
        phase: this.state.address ? 'CONNECTED' : 'ANONYMOUS',
        session: null,
      })
      return
    }
    const value = session as AuthenticatedSession
    this.patch({
      phase: 'AUTHENTICATED',
      session: value,
      address: value.address,
    })
  }

  /**
   * Sign-out, in the order docs/04 §3.2.4 specifies.
   *
   * The key is forgotten locally but **not revoked** — logging out on a laptop
   * must not kill a key the user is relying on elsewhere, and the key is useless
   * to anyone who does not hold its private material.
   */
  applyLogout(): void {
    this.patch({
      phase: 'ANONYMOUS',
      address: null,
      chainId: null,
      session: null,
      key: 'ABSENT',
      keyAddress: null,
      keyExpiresAtMs: null,
      keyNotice: null,
    })
  }

  applyKey(input: { state: KeyState; address?: string | null; expiresAtMs?: number | null }): void {
    this.patch({
      key: input.state,
      keyAddress: input.address ?? (input.state === 'ABSENT' ? null : this.state.keyAddress),
      keyExpiresAtMs: input.expiresAtMs ?? (input.state === 'ABSENT' ? null : this.state.keyExpiresAtMs),
    })
  }

  /**
   * The username the venue just confirmed, onto the session it belongs to
   * (`docs/06` §F136). A name never changes once set, so the session's copy
   * is good for the rest of the session and the header can show it now
   * rather than at the next `GET /auth/session`.
   *
   * Only if the session is still the account the name was claimed for: a
   * claim that lands after a sign-out, or after signing in as another
   * wallet, must not put one account's name on another.
   */
  applyDisplayName(address: string, displayName: string): void {
    const session = this.state.session
    if (!session || session.address.toLowerCase() !== address.toLowerCase()) return
    if (session.display_name === displayName) return
    this.patch({ session: { ...session, display_name: displayName } })
  }

  dropKey(reason: string | null): void {
    this.patch({ key: 'ABSENT', keyAddress: null, keyExpiresAtMs: null, keyNotice: reason })
  }

  clearNotice(): void {
    if (this.state.keyNotice !== null) this.patch({ keyNotice: null })
  }
}

// ---------------------------------------------------------------------------
// Derived predicates. Kept here so the header and the ticket cannot disagree.
// ---------------------------------------------------------------------------

/**
 * The session and the wallet disagree about who this is — `docs/17`.
 *
 * `applySession` takes `session.address` as authoritative, and it has to: on a
 * cold load the cookie is the only thing that exists, and the wallet stack is
 * not loaded (invariant 13). What was missing is the comparison **once both are
 * known**. Nothing in the tree watched the wallet's account, so a user who
 * switched account after signing in kept trading the old one: the header, the
 * balance and the positions all render the session's address, and the order's
 * `maker` is that address too — so the screen is self-consistent and wrong, and
 * the only place the truth appears is the wallet's own UI.
 *
 * Returns `null` when there is nothing to compare, which is every anonymous
 * load and every tab whose wallet stack has not been asked for. A mismatch is
 * **not** an error state to recover from automatically: which of the two the
 * user meant is theirs to say, and signing back in as the connected wallet is
 * one click away.
 *
 * Case-insensitive, because these arrive from three sources with three
 * conventions — EIP-55 from the wallet, lowercase from most of the wire — and a
 * checksum difference is not a different account.
 */
export function accountMismatch(
  state: IdentityState,
): { session: string; wallet: string } | null {
  const session = state.session?.address ?? null
  const wallet = state.walletAddress
  if (!session || !wallet) return null
  if (session.toLowerCase() === wallet.toLowerCase()) return null
  return { session, wallet }
}

/**
 * The signed-in account's username, or `null` — signed out, or no name yet.
 * Read by the header chip, the account menu and Settings, so all three agree.
 */
export function displayNameOf(state: IdentityState): string | null {
  if (state.phase !== 'AUTHENTICATED') return null
  const name = state.session?.display_name
  return typeof name === 'string' && name.length > 0 ? name : null
}

/** Can this tab place an order without raising a popup? */
export function canTradeSilently(state: IdentityState): boolean {
  if (accountMismatch(state)) return false
  return state.phase === 'AUTHENTICATED' && (state.key === 'ARMED' || state.key === 'ROTATE_DUE')
}

/**
 * What the ticket's primary control should say.
 *
 * One function rather than a chain of ternaries in the component, because the
 * copy is specified (docs/07 §6) and the ordering of the checks is a product
 * decision: a user who is signed in but unarmed sees "Enable trading", not
 * "Sign in", even though both are true of them in some sense.
 */
export type TicketGate = 'CONNECT' | 'SIGN_IN' | 'WRONG_ACCOUNT' | 'WRONG_CHAIN' | 'ENABLE_TRADING' | 'READY'

/**
 * `wrongChain` is `useWallet().chainMismatch !== null`. It only matters to
 * `ENABLE_TRADING`: the wallet signs the session key, and a wallet on another
 * network refuses that signature. Once a key is armed the wallet's network no
 * longer affects trading, because the key signs orders and it has no chain, so
 * `READY` is never held back.
 */
export function ticketGate(state: IdentityState, wrongChain = false): TicketGate {
  if (state.phase === 'ANONYMOUS') return 'CONNECT'
  if (state.phase !== 'AUTHENTICATED') return 'SIGN_IN'
  // Above `ENABLE_TRADING` deliberately: arming binds a key to the *session's*
  // account, so offering it to a user whose wallet has moved on spends a popup
  // on the account they are no longer using. The mismatch is the thing to fix.
  if (accountMismatch(state)) return 'WRONG_ACCOUNT'
  if (state.key === 'ABSENT') return wrongChain ? 'WRONG_CHAIN' : 'ENABLE_TRADING'
  return 'READY'
}
